import { AuthClient } from './client';
import { AuthError, type AuthErrorCode, type AuthSession } from './types';
import { LOGIN_COPY, LOGIN_ERRORS } from './loginCopy';
import { HTML_LANG, loadLang, saveLang, type Lang } from '../scenes/hub/hubCopy';
import logoEn from '../assets/landing/academy-horizontal-en.png';
import logoZh from '../assets/landing/academy-horizontal-zh.png';
import { el } from '../ui/dom';
import './loginStyles.css';

export interface LoginPageOptions {
  readonly initialError?: AuthErrorCode;
}

/** Upstream landing design, connected to the Academy's server authentication. */
export function showLoginPage(
  root: HTMLElement,
  client: AuthClient,
  options: LoginPageOptions = {}
): Promise<AuthSession> {
  return new Promise((resolve) => {
    root.classList.add('stage--auth');
    let lang: Lang = loadLang();
    let busy = false;
    let currentError = options.initialError ?? null;
    const email = el('input', {
      class: 'auth-input',
      attrs: {
        id: 'auth-email', name: 'email', type: 'email', autocomplete: 'username',
        required: true, placeholder: 'name@school.edu.hk', autocapitalize: 'none',
        spellcheck: 'false', 'aria-describedby': 'auth-error',
      },
    });
    const password = el('input', {
      class: 'auth-input',
      attrs: {
        id: 'auth-password', name: 'password', type: 'password',
        autocomplete: 'current-password', required: true, 'aria-describedby': 'auth-error',
      },
    });

    const render = (): void => {
      const copy = (key: keyof typeof LOGIN_COPY): string => LOGIN_COPY[key][lang];
      document.documentElement.lang = HTML_LANG[lang];
      password.placeholder = copy('passwordPlaceholder');
      const error = el('p', {
        id: 'auth-error', class: 'auth-error',
        text: currentError ? LOGIN_ERRORS[currentError][lang] : '',
        attrs: { role: 'alert', hidden: !currentError },
      });
      const reveal = el('button', {
        class: 'auth-reveal', text: copy(password.type === 'password' ? 'show' : 'hide'),
        attrs: { type: 'button', 'aria-controls': 'auth-password', 'aria-pressed': String(password.type === 'text') },
        on: { click: () => {
          const visible = password.type === 'password';
          password.type = visible ? 'text' : 'password';
          reveal.textContent = copy(visible ? 'hide' : 'show');
          reveal.setAttribute('aria-pressed', String(visible));
        } },
      });
      const submit = el('button', {
        class: 'auth-submit', text: copy('signIn'), attrs: { type: 'submit' },
      });
      const language = el('button', {
        class: 'auth-language', text: 'Language 中/EN',
        attrs: { type: 'button', 'aria-label': lang === 'zh' ? 'Switch to English' : '切換繁體中文' },
        on: { click: () => {
          if (busy) return;
          lang = lang === 'en' ? 'zh' : 'en';
          saveLang(lang);
          render();
          root.querySelector<HTMLButtonElement>('.auth-language')?.focus();
        } },
      });
      const form = el('form', {
        class: 'auth-form', attrs: { 'aria-labelledby': 'auth-title' },
        on: { submit: (event) => {
          event.preventDefault();
          if (busy) return;
          busy = true;
          currentError = null;
          error.hidden = true;
          form.setAttribute('aria-busy', 'true');
          for (const control of [submit, language, email, password]) control.disabled = true;
          submit.textContent = copy('loading');
          void client.signInWithCredentials(email.value.trim(), password.value)
            .then((session) => {
              email.value = '';
              password.value = '';
              root.replaceChildren();
              root.classList.remove('stage--auth');
              resolve(session);
            })
            .catch((reason: unknown) => {
              currentError = reason instanceof AuthError ? reason.code : 'service_unavailable';
              error.textContent = LOGIN_ERRORS[currentError][lang];
              error.hidden = false;
            })
            .finally(() => {
              busy = false;
              form.removeAttribute('aria-busy');
              for (const control of [submit, language, email, password]) control.disabled = false;
              submit.textContent = copy('signIn');
              if (currentError) password.focus();
            });
        } },
      },
      el('h1', { id: 'auth-title', text: copy('title') }),
      el('p', { class: 'auth-intro', text: copy('subtitle') }),
      el('label', { text: copy('email'), attrs: { for: 'auth-email' } }), email,
      el('label', { text: copy('password'), attrs: { for: 'auth-password' } }),
      el('div', { class: 'auth-password' }, password, reveal), error, submit,
      el('p', { class: 'auth-provisioning-note', text: copy('note') }));

      const noticeFooter = (): HTMLElement => el('footer', { class: 'auth-legal' },
        el('span', { text: '© 2026 The Coca-Cola Company | ' }),
        el('a', { text: copy('importantNotice'), attrs: { href: `/important-notice.html#${lang}` } }));
      root.replaceChildren(el('main', { class: 'auth-page', attrs: { 'data-testid': 'login-page' } },
        el('div', { class: 'auth-shell' },
          el('header', { class: 'auth-brand' },
            el('div', { class: 'auth-logo' },
              el('img', { attrs: { src: lang === 'en' ? logoEn : logoZh, alt: copy('brand'), draggable: 'false' } })),
            language),
          el('div', { class: 'auth-content' },
            el('section', { class: 'auth-welcome' },
              el('span', { class: 'auth-eyebrow', text: 'LEARN • PLAY • RECYCLE' }),
              el('h2', { text: copy('welcome') }),
              el('p', { text: copy('intro') }),
              el('span', { class: 'auth-game-count', text: copy('games') }),
              noticeFooter()),
            form),
          el('div', { class: 'auth-legal-mobile' }, noticeFooter()))));
    };
    render();
  });
}
